/**
 * 会员卡充值
 */
import React, { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Button,
  Col,
  Descriptions,
  Form,
  message,
  Popconfirm,
  Row,
  Skeleton,
  Table,
  Tag,
} from 'antd';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';
import getRandomId from '@/utils/uuid';
import {
  format2Decimal,
  isNil,
  parse2Number,
  parseYuan2Fen,
  parseFen2Yuan,
  parseFen2Currency,
} from '@/utils/number';
import { getActivityList, getActivityOrder, updateActivityInfo } from '@/redux/actions/guest';
import { GuestInitialStateProps, Iactivity, IguestInfo } from '@/redux/reducers/guest';
import { fetchCustomerInfoByWyuid, fetchCustomerInfo } from '@/api/guest';
import { renderIntlLabel, storage } from '@/utils';
import { ContractType } from '@/config';
import CustomDrawer, { IdrawerRef } from '../CustomDrawer';
import InputMoney from '../InputMoney';
import { createRechargeValidator } from '../InputMoney/validator';
import Payment from '../Payment';
import PayModal from '../Payment/payModal';
import { PayType, PayModalRef, PayWay } from '../Payment/interface';
import CustomForm, { IFieldItem, IMyFormRef } from '../CustomForm';
import BaseMobileInput from '../BaseMobileInput';
import BaseEmailInput, { EMAIL_REG } from '../BaseEmailInput';
import { parseAreaMobileStr } from '../BaseMobileInput/helper';
import { TenantGlobalConfig } from '../Intl/IntlContext/helper';
import styles from './index.module.scss';

let timer = null;

export interface IcardRechargeRef extends Omit<IdrawerRef, 'openDrawer'> {
  openDrawer: (value: {
    isNew?: boolean;
    wyuid: string;
    accountType: any;
    name?: string;
    certType?: any;
    certNo?: string;
    mobile?: string;
    email?: string;
  }) => void;
}

interface IcardRechargeState extends Partial<GuestInitialStateProps> {
  fnGetActivityList?: (value: any) => void;
  updateActivityInfoFn?: (...args: any[]) => void;
  fnGetActivityOrder?: (value: any) => Promise<any>;
}

interface IcardRechargeProps extends IcardRechargeState {
  myRef?: any;
  onCloseDrawer?: (isRechargeSuccess: boolean) => void;
}

type CertInfo = Partial<
  {
    isNew: boolean;
  } & Pick<IguestInfo, 'name' | 'certType' | 'certNo' | 'mobile' | 'email' | 'wyuid'>
>;

const CardRecharge: React.FC<IcardRechargeProps> = ({
  myRef,
  activityList,
  onCloseDrawer,
  fnGetActivityList,
  updateActivityInfoFn,
  fnGetActivityOrder,
  activityListLoading,
  activityOrderLoading,
}) => {
  const [form] = Form.useForm();

  const [memberInfo, setMemberInfo] = useState<Partial<CertInfo>>({});

  // 输入金额，以元为单位
  const [money, setMoney] = useState<number | string>();
  const [selectedActivityList, setSelectedActivityList] = useState<
    Array<Iactivity & { id: string }>
  >([]);

  // 收银台
  const [selectedPayType, setSelectedPayType] = useState<PayType>(3);

  const drawerRef = useRef<IdrawerRef>();
  const wyuidRef = useRef<string>();
  const accountTypeRef = useRef<string>();
  const payModalRef = useRef<PayModalRef>();
  const detailRef = useRef<IMyFormRef>();

  // 已选总金额
  const [selectedTotalMoney, couponTotalMap] = useMemo(() => {
    let totalMoney = 0;
    // 统计的各种券总数
    const couponMap = {};
    if (Array.isArray(selectedActivityList)) {
      selectedActivityList.forEach((item) => {
        const tempSum = totalMoney + Number(item.conditionOrderAmountFloor || 0);
        totalMoney = Number(tempSum);

        item.couponList?.forEach((couponItem) => {
          if (couponItem.couponCode) {
            if (!couponMap[couponItem.couponCode]) {
              couponMap[couponItem.couponCode] = {
                couponCount: couponItem.couponCount,
                couponName: couponItem.couponName,
              };
            } else {
              couponMap[couponItem.couponCode] = Object.assign(couponMap[couponItem.couponCode], {
                couponCount: couponMap[couponItem.couponCode].couponCount + couponItem.couponCount,
              });
            }
          }
        });
      });
    }
    return [totalMoney, couponMap];
  }, [selectedActivityList]);

  const remainingMoney = useMemo(() => {
    return parse2Number(parseYuan2Fen(money || 0)) - selectedTotalMoney;
  }, [money, selectedTotalMoney]);

  const detailAttr: Array<IFieldItem> = useMemo(() => {
    return [
      {
        label: intl.get('bill_refound_res_mobile_no'),
        key: 'mobile',
        type: 'group',
        rules: [
          {
            validator(_, val) {
              const { areaCode, mobileStr } = parseAreaMobileStr(val);
              // 只填一个
              if ((areaCode && !mobileStr) || (!areaCode && mobileStr)) {
                return Promise.reject(
                  new Error(renderIntlLabel('reservation_error_mobile_invalid')),
                );
              }
              // 是否修改过，修改过校验是否脱敏
              if (val && val !== memberInfo?.mobile && val.includes('*')) {
                return Promise.reject(
                  new Error(renderIntlLabel('reservation_error_mobile_invalid')),
                );
              }
              // 关键人常用联系方式手机号是否必填
              if (
                TenantGlobalConfig.getSystemConfig().contractType === ContractType.MOBILE &&
                !val
              ) {
                return Promise.reject(
                  new Error(renderIntlLabel('reservation_error_mobile_invalid')),
                );
              }

              return Promise.resolve();
            },
          },
        ],
        component: <BaseMobileInput />,
      },
      {
        label: intl.get('help_store_address_type_email'),
        key: 'email',
        type: 'group',
        rules: [
          {
            validator(_, val) {
              // 是否修改过，修改过校验邮箱格式是否正确
              if (val && val !== memberInfo?.email && !EMAIL_REG.test(val)) {
                return Promise.reject(
                  new Error(
                    renderIntlLabel('order_frontDesk_validate_please_check_email_error_format'),
                  ),
                );
              }
              if (
                TenantGlobalConfig.getSystemConfig().contractType === ContractType.EMAIL &&
                !val
              ) {
                return Promise.reject(
                  new Error(renderIntlLabel('reservation_error_mobile_invalid')),
                );
              }
              return Promise.resolve();
            },
          },
        ],
        component: <BaseEmailInput />,
      },
    ];
  }, [memberInfo]);

  // 是否存在余额且充送活动次数没有限制 ｜｜ 已使用次数小于限制次数
  const isRemainMoneyAndHasJoinCount = (remaining, item) => {
    // 99999无限制
    return (
      Number(item.conditionOrderAmountFloor || 0) <= remaining &&
      (item.limitCount === 99999 || (item.useCount >= 0 && item.useCount < item.limitCount))
    );
  };

  // 左侧活动
  const dispalyActivityList = useMemo(() => {
    if (!money) {
      return activityList;
    }
    return (activityList || []).filter((item) =>
      isRemainMoneyAndHasJoinCount(remainingMoney, item),
    );
  }, [money, remainingMoney, activityList]);

  const getMobileByCertNo = async (params) => {
    // console.log('getMobileByCertNo', params);
    const { name, certType, certNo, wyuid } = params;
    if ((params.isNew && (!name || !certType || !certNo)) || (!params.isNew && !wyuid)) {
      setMemberInfo(params);
      return;
    }
    // 根据证件号查询手机号、备注
    try {
      const result = { ...params };
      let res;
      if (params.isNew) {
        res = await fetchCustomerInfo({ certType, certNo });
      } else {
        res = await fetchCustomerInfoByWyuid({ wyuid });
      }
      const data = res?.data || {};
      const { wyuid: currentWyuid, certType: type, mobile, remark, email } = data;
      result.wyuid = currentWyuid;
      if (type) {
        result.certType = type;
      }
      if (mobile) {
        result.mobile = mobile;
      }
      if (remark) {
        result.remark = remark;
      }
      if (email) {
        result.email = email;
      }
      if (!name && data?.name) {
        result.name = data?.name;
      }
      if (!certNo && data?.certNo) {
        result.certNo = data?.certNo;
      }
      setMemberInfo(result);
    } catch (error) {
      setMemberInfo(params);
    }
  };

  const handleOpenDrawer = async (info) => {
    drawerRef.current.openDrawer();
    form.setFieldsValue({ money: '' });
    setMoney('');

    // 获取个人信息
    getMobileByCertNo(info);

    // 清空右侧已选活动列表
    setSelectedActivityList([]);
    const { wyuid, accountType } = info;
    wyuidRef.current = wyuid;
    accountTypeRef.current = accountType;
    if (wyuid) {
      const params = { wyuid, amount: -1 };
      fnGetActivityList(params);
    } else {
      message.error(intl.get('cpm_card_recharge_wyuid_cannot_empty'));
    }
  };

  const handleCancel = () => {
    drawerRef.current.closeDrawer();
    setMoney('');
    // 清空右侧已选活动列表
    setSelectedActivityList([]);
    wyuidRef.current = '';
    accountTypeRef.current = '';
  };

  const initActivityListDebounce = useCallback(
    debounce((val) => {
      if (wyuidRef.current) {
        const params = {
          wyuid: wyuidRef.current,
          amount: parse2Number(parseYuan2Fen(val) || -1),
        };
        // 清空右侧已选活动列表
        setSelectedActivityList([]);

        fnGetActivityList(params);
      }
    }, 1000),
    [],
  );

  const onMoneyChange = async () => {
    clearTimeout(timer);
    timer = setTimeout(async () => {
      try {
        const values = await form.validateFields();
        const { money: val } = values || {};
        setMoney(val);
        initActivityListDebounce(val);
      } catch (error) {
        setMoney('');
        initActivityListDebounce('');
      }
    }, 0);
  };

  const addActivityDebounce = useCallback(
    debounce((activity) => {
      if (isRemainMoneyAndHasJoinCount(remainingMoney, activity)) {
        // 原活动已使用次数加1
        updateActivityInfoFn(activity, 'add');

        // 可重复选择活动，需前端生成ID
        setSelectedActivityList([...selectedActivityList, { ...activity, id: getRandomId() }]);
      } else {
        message.error(intl.get('cpm_card_recharge_balance_unenough'));
      }
    }, 200),
    [selectedActivityList, remainingMoney],
  );

  const handleAddActivity = async (activity: Iactivity) => {
    addActivityDebounce(activity);
  };

  const handleDelete = (record) => {
    // 原活动已使用次数减1
    updateActivityInfoFn(record, 'delete');

    let tempList = [...selectedActivityList];
    tempList = tempList.filter((item) => item.id !== record.id);
    setSelectedActivityList(tempList);
  };

  const handleSelectPayType = (payType) => {
    setSelectedPayType(payType);
  };

  const payResultCallback = ({ generatePayOrder, payed, result }) => {
    if (generatePayOrder) {
      drawerRef.current.closeDrawer();
      return;
    }
    if (payed && result) {
      message.success(intl.get('cpm_card_recharge_vip_card_success'));
      setMoney('');
      if (onCloseDrawer) {
        onCloseDrawer(true);
      }
      drawerRef.current.closeDrawer();
    }
    if (payed && !result) {
      if (onCloseDrawer) {
        onCloseDrawer(false);
      }
    }
  };

  // 下单
  const openPayModal = async (confirm?: boolean) => {
    await detailRef.current.validateFields();

    if (!wyuidRef.current) {
      message.error(intl.get('cpm_card_recharge_user_no_vipuid'));
      return;
    }
    if (!money) {
      message.error(intl.get('cpm_card_recharge_input_money'));
      return;
    }
    // 付款单需confirm后下单
    if (!confirm && selectedPayType === PayWay.Fukuandan) {
      payModalRef.current.openModal({
        payType: selectedPayType,
        showFukuanDanConfirm: true,
      });
      return;
    }
    const { email, mobile } = detailRef.current?.getFieldsValue();
    const activityCodes = selectedActivityList?.map((item) => item.promotionCode);
    const params = {
      fkdFlag: selectedPayType === PayWay.Fukuandan,
      wyuid: wyuidRef.current,
      amount: parse2Number(parseYuan2Fen(money || 0)),
      activityCodes,
      activities: selectedActivityList?.map((item) => ({
        code: item.promotionCode,
        name: item.promotionName,
      })),
      email,
      mobile,
    };
    fnGetActivityOrder(params).then((data) => {
      payModalRef.current.openModal({
        payAmount: parse2Number(parseYuan2Fen(money || 0)),
        payType: selectedPayType,
        payOrderType: 101,
        paymentNo: data,
        businessType: 3,
        wyuid: wyuidRef.current,
        accountType: Number(accountTypeRef.current || 0),
      });
    });
  };

  const handleReceiveMoneyDebounce = debounce((confirm) => {
    openPayModal(confirm);
  }, 200);

  useImperativeHandle(
    myRef,
    () => ({
      ...drawerRef.current,
      openDrawer: handleOpenDrawer,
    }),
    [],
  );

  const columns: ColumnsType<Iactivity & { id: string }> = [
    {
      title: intl.get('channel_activity_name'),
      dataIndex: 'promotionName',
    },
    {
      title: intl.get('handover_recharge_indicator_total'),
      dataIndex: 'conditionOrderAmountFloor',
      render: (val) => (val ? parseFen2Yuan(val) : '-'),
    },
    {
      title: intl.get('cpm_card_recharge_gifts'),
      dataIndex: 'promotionDescription',
      render: (_, record) => (
        <>
          {record?.couponList?.map((item) => (
            <div>{`${item.couponName || ''}${
              item.couponCount ? ` x ${item.couponCount}` : ''
            }`}</div>
          ))}
        </>
      ),
    },
    {
      title: intl.get('app_table_column_action'),
      render: (_, record) => (
        <Button type="link" onClick={() => handleDelete(record)}>
          {intl.get('app_table_content_delete_btn')}
        </Button>
      ),
      fixed: 'right',
      width: 60,
      align: 'right',
    },
  ];

  // 有充送活动但是未选
  const isExsitUnSelectedActivity = () => {
    if (
      Array.isArray(dispalyActivityList) &&
      dispalyActivityList.length &&
      (!selectedActivityList || !selectedActivityList?.length)
    ) {
      return true;
    }
    return false;
  };

  return (
    <>
      <CustomDrawer
        width="90%"
        title={intl.get('cpm_card_recharge_vip')}
        footer={
          <>
            {!isNil(money) && isExsitUnSelectedActivity() ? (
              <Popconfirm
                placement="topRight"
                title={intl.get('cpm_card_recharge_continue_select_activity')}
                onConfirm={() => handleReceiveMoneyDebounce()}
                okButtonProps={{ loading: activityOrderLoading }}
              >
                <Button type="primary">{intl.get('bill_credit_collection')}</Button>
              </Popconfirm>
            ) : (
              <Button
                type="primary"
                loading={activityOrderLoading}
                onClick={() => handleReceiveMoneyDebounce()}
              >
                {intl.get('bill_credit_collection')}
              </Button>
            )}
            <Button onClick={handleCancel}>{intl.get('app_confirm_cancel_btn')}</Button>
          </>
        }
        onClose={handleCancel}
        ref={drawerRef}
      >
        <div className={styles.container}>
          <div className="space-between" style={{ marginBottom: '32px' }}>
            <div className="groupLabel">{intl.get('cpm_card_purchase_vip_info')}</div>
          </div>
          <>
            {/* <Descriptions title="" column={1}>
              <Descriptions.Item label={intl.get('app_name')}>{memberInfo.name}</Descriptions.Item>
              <Descriptions.Item label={intl.get('app_reservation_cert_type')}>
                {TenantGlobalConfig.getSystemConfig().certTypesMap?.[memberInfo.certType]}
              </Descriptions.Item>
              <Descriptions.Item label={intl.get('app_reservation_cert_number')}>
                {memberInfo.certNo}
              </Descriptions.Item>
            </Descriptions> */}
            <Form.Item labelCol={{ span: 2 }} labelAlign="left" label={intl.get('app_name')}>
              {memberInfo.name}
            </Form.Item>
            <Form.Item
              labelCol={{ span: 2 }}
              labelAlign="left"
              label={intl.get('app_reservation_cert_type')}
            >
              {TenantGlobalConfig.getSystemConfig().certTypesMap?.[memberInfo.certType]}
            </Form.Item>
            <Form.Item
              labelCol={{ span: 2 }}
              labelAlign="left"
              label={intl.get('app_reservation_cert_number')}
            >
              {memberInfo.certNo}
            </Form.Item>
            <CustomForm
              ref={detailRef}
              labelCol={{ span: 2 }}
              labelAlign="left"
              wrapperCol={{ span: 10 }}
              formAttr={detailAttr}
              value={memberInfo}
            />
          </>

          <div className="space-between" style={{ marginBottom: '32px' }}>
            <div className="groupLabel">{intl.get('cpm_card_recharge_info')}</div>
          </div>
          <Form form={form} initialValues={{ money }}>
            <Form.Item
              label={intl.get('handover_recharge_indicator_total')}
              labelCol={{ span: 2 }}
              labelAlign="left"
              wrapperCol={{ span: 8 }}
              name="money"
              rules={[
                { required: true, message: intl.get('cpm_card_recharge_input_money') },
                {
                  validator:
                    storage.get('commonCode') === 'DJ9981' ? null : createRechargeValidator(),
                },
              ]}
            >
              <InputMoney onChange={onMoneyChange} requiredBlur={false} />
            </Form.Item>
          </Form>
          <Row>
            <Col span={8}>
              <div className={styles.left}>
                <div className={styles.leftTitle}>
                  {intl.get('cpm_card_recharge_select_discount_activity')}
                </div>
                <div className={styles.scrollContent}>
                  <Skeleton loading={activityListLoading}>
                    {dispalyActivityList?.map((item) => (
                      <div
                        key={item.promotionCode}
                        className={`${styles.scrollItem} space-between`}
                      >
                        <div>
                          <div className={styles.itemTitle}>{item.promotionName}</div>
                          <div className={styles.itemContent}>
                            <div>
                              <Tag>{intl.get('cpm_card_recharge_times')}</Tag>
                              {item.joinCountText}
                            </div>
                            <div>
                              <Tag>{intl.get('cpm_card_recharge')}</Tag>
                              {parseFen2Currency(item.conditionOrderAmountFloor || 0)}
                            </div>
                            <div className="flex-vcenter ">
                              <Tag style={{ height: 22 }}>{intl.get('cpm_card_recharge_send')}</Tag>
                              <div>
                                {item?.couponList?.map((couponItem) => (
                                  <div className={styles.coupon}>{`${couponItem.couponName || ''}${
                                    !isNil(couponItem.couponCount)
                                      ? ` x ${couponItem.couponCount}`
                                      : ''
                                  }`}</div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div style={{ cursor: 'pointer' }} onClick={() => handleAddActivity(item)}>
                          <PlusOutlined />
                        </div>
                      </div>
                    ))}
                  </Skeleton>
                </div>
              </div>
            </Col>
            <Col span={16}>
              <Table
                rowKey="id"
                columns={columns}
                dataSource={selectedActivityList}
                pagination={false}
                scroll={{ y: 346 }}
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell key={0} index={0}>
                        {intl.get('cpm_card_recharge_subtotal')}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell key={1} index={1}>
                        {parseFen2Yuan(selectedTotalMoney || 0)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell key={2} index={2} colSpan={2}>
                        {Object.keys(couponTotalMap).map((key) => (
                          <div key={key}>
                            {couponTotalMap[key]?.couponName} x {couponTotalMap[key]?.couponCount}
                          </div>
                        ))}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            </Col>
          </Row>

          <div className="space-between" style={{ marginTop: '64px', marginBottom: '32px' }}>
            <div className="groupLabel">{intl.get('bill_credit_cashier')}</div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <span className="text-colon">{intl.get('bill_credit_account_receivable')}</span>
            {!isNil(money) ? <span className="amount-pay">{format2Decimal(money || 0)}</span> : '—'}
          </div>
          <Payment isInitPayTypes businessType={3} selectPayType={handleSelectPayType} />
          <PayModal
            ref={payModalRef}
            payResultCallback={payResultCallback}
            onConfirmOk={() => handleReceiveMoneyDebounce(true)}
          />
        </div>
      </CustomDrawer>
    </>
  );
};

const mapStateToProps = (state) => {
  return { ...state.guest };
};

const ConnectComponent: React.FC<IcardRechargeProps> = connect(mapStateToProps, {
  fnGetActivityList: getActivityList,
  updateActivityInfoFn: updateActivityInfo,
  fnGetActivityOrder: getActivityOrder,
})(React.memo(CardRecharge));

export default React.forwardRef<IcardRechargeRef, IcardRechargeProps>((props, ref) => (
  <ConnectComponent {...props} myRef={ref} />
));
